import React, { ReactNode } from "react";

export interface GettingStartedStep {
  icon: ReactNode;
  title: string;
  body: string;
  mockup?: ReactNode;
  tip?: ReactNode;
  className?: string;
}

interface Props {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  steps: GettingStartedStep[];
  footer?: ReactNode;
  banner?: ReactNode;
}

const GettingStarted: React.FC<Props> = ({
  icon,
  title,
  subtitle,
  steps,
  footer,
  banner,
}) => {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 lg:p-10">
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-blue-50 text-blue-600 mb-3 sm:mb-4">
          {icon}
        </div>
        <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 leading-tight">
          {title}
        </h2>
        {subtitle && (
          <p className="text-xs sm:text-sm text-gray-600 mt-2">{subtitle}</p>
        )}
      </div>

      <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-6 sm:mt-8 max-w-6xl mx-auto">
        {steps.map((step, i) => (
          <li
            key={i}
            className={`relative bg-gray-50 border border-gray-200 rounded-xl p-4 pt-6 min-w-0 ${
              step.className || ""
            }`}
          >
            <span className="absolute -top-3 left-4 inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold shadow-sm">
              {i + 1}
            </span>
            <div className="flex items-center gap-2 text-blue-600 mb-2">
              <span className="flex-shrink-0">{step.icon}</span>
              <h3 className="text-sm font-semibold text-gray-900 leading-tight">
                {step.title}
              </h3>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed break-words">
              {step.body}
            </p>
            {step.mockup}
            {step.tip && (
              <p className="text-xs text-gray-500 mt-2 leading-snug break-words">
                {step.tip}
              </p>
            )}
          </li>
        ))}
      </ol>

      {banner}

      {footer && (
        <div className="flex justify-center mt-6 sm:mt-8">{footer}</div>
      )}
    </div>
  );
};

export default GettingStarted;
